import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { businessInsurancePage, companyInfo } from '../mock';
import { CheckCircle2, Phone, AlertCircle, ExternalLink } from 'lucide-react';
import { Card, CardContent } from './ui/card';

const BusinessInsurance = () => {
  const { language } = useLanguage();
  const content = businessInsurancePage[language];
  const [iframeError, setIframeError] = useState(false);

  const thimbleUrl = 'https://app.thimble.com/home?brokerid=PKW6KYVV7&widget_source=embed';

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-purple-700 to-purple-900 text-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              {content.title}
            </h1>
            <p className="text-xl md:text-2xl text-purple-100 mb-8">
              {content.subtitle}
            </p>
            <p className="text-lg text-purple-50">
              {content.description}
            </p>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-gray-900">
            {content.howItWorks.title}
          </h2>
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {content.howItWorks.steps.map((step) => (
              <Card key={step.step} className="text-center border-none shadow-lg">
                <CardContent className="p-8">
                  <div className="w-16 h-16 bg-gradient-to-br from-purple-600 to-purple-700 rounded-full flex items-center justify-center mx-auto mb-6 text-white text-2xl font-bold">
                    {step.step}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    {step.title}
                  </h3>
                  <p className="text-gray-600">
                    {step.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Widget Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Card className="shadow-2xl border-none">
              <CardContent className="p-8 md:p-12">
                <h2 className="text-3xl font-bold text-center mb-4 text-gray-900">
                  {language === 'en' ? 'Get Your Quote Now' : 'Obtenga Su Cotización Ahora'}
                </h2>
                
                {/* Quote Options */}
                <div className="mb-8">
                  <p className="text-center text-gray-600 mb-6">
                    {language === 'en' 
                      ? 'Choose your preferred quote option:' 
                      : 'Elija su opción de cotización preferida:'}
                  </p>
                  
                  <div className="grid md:grid-cols-2 gap-4 mb-6">
                    {/* Thimble Option */}
                    <div className="bg-gradient-to-br from-purple-50 to-purple-100 border-2 border-purple-300 rounded-xl p-6 text-center">
                      <div className="flex justify-center mb-3">
                        <img 
                          src="https://www.thimble.com/wp-content/themes/thimble-neue/assets/img/logo-lockup.png" 
                          alt="Thimble"
                          className="h-8 w-auto object-contain"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextElementSibling.style.display = 'block';
                          }}
                        />
                        <h3 className="text-xl font-bold text-purple-900 hidden">
                          Thimble
                        </h3>
                      </div>
                      <p className="text-sm text-purple-700 mb-4">
                        {language === 'en' 
                          ? 'Flexible, on-demand coverage' 
                          : 'Cobertura flexible bajo demanda'}
                      </p>
                      <p className="text-xs text-gray-600">
                        {language === 'en' 
                          ? 'Use the form below' 
                          : 'Use el formulario de abajo'}
                      </p>
                    </div>

                    {/* NEXT Insurance Option */}
                    <div className="bg-gradient-to-br from-blue-50 to-cyan-100 border-2 border-blue-300 rounded-xl p-6 text-center">
                      <div className="flex justify-center mb-3">
                        <img 
                          src="/images/partners/next-insurance-logo.svg" 
                          alt="NEXT Insurance"
                          className="h-8 w-auto object-contain"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextElementSibling.style.display = 'block';
                          }}
                        />
                        <h3 className="text-xl font-bold text-blue-900 hidden">
                          NEXT Insurance
                        </h3>
                      </div>
                      <p className="text-sm text-blue-700 mb-4">
                        {language === 'en' 
                          ? 'Fast online quote and coverage' 
                          : 'Cotización y cobertura rápida en línea'}
                      </p>
                      <a
                        href="https://track.nextinsurance.com/links?agent_affiliation=0KsytGMUJRVzBBra&serial=992855993&channel=affiliation"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-full font-bold hover:bg-blue-700 transition-all shadow-lg hover:shadow-xl hover:scale-105 transform"
                      >
                        {language === 'en' ? 'Get a NEXT Quote' : 'Cotizar con NEXT'}
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    </div>
                  </div>

                  <div className="bg-amber-50 border-l-4 border-amber-500 p-4 rounded-lg">
                    <p className="text-xs text-gray-700">
                      <strong className="text-amber-800">
                        {language === 'en' ? 'Important:' : 'Importante:'}
                      </strong>{' '}
                      {language === 'en' 
                        ? 'Quotes and pricing are subject to underwriting approval. Coverage availability may vary by location and business type.' 
                        : 'Las cotizaciones y precios están sujetos a aprobación de suscripción. La disponibilidad de cobertura puede variar según ubicación y tipo de negocio.'}
                    </p>
                  </div>
                </div>

                <h3 className="text-2xl font-bold text-center mb-4 text-gray-900">
                  {language === 'en' ? 'Thimble Quote Form' : 'Formulario de Cotización Thimble'}
                </h3>
                
                {/* Info Note */}
                <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-lg mb-6">
                  <p className="text-sm text-blue-800">
                    <strong>ℹ️ {language === 'en' ? 'Note:' : 'Nota:'}</strong> {content.widgetNote}
                  </p>
                </div>

                {/* Thimble iframe Widget */}
                {!iframeError ? (
                  <div className="flex justify-center items-center mb-6">
                    <div className="w-full max-w-md">
                      <iframe
                        src={thimbleUrl}
                        title="Thimble Business Insurance Quote"
                        className="w-full rounded-lg shadow-md"
                        style={{
                          height: '700px',
                          border: '0',
                          maxWidth: '100%',
                          maxHeight: '100%'
                        }}
                        allow="geolocation *;"
                        onError={() => setIframeError(true)}
                      />
                    </div>
                  </div>
                ) : (
                  <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-lg mb-6">
                    <div className="flex items-start">
                      <AlertCircle className="w-6 h-6 text-red-500 mr-3 flex-shrink-0 mt-1" />
                      <div>
                        <p className="text-red-800 font-semibold mb-3">
                          {language === 'en' 
                            ? 'Unable to load the quote form.' 
                            : 'No se puede cargar el formulario de cotización.'}
                        </p>
                        <a 
                          href={thimbleUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 text-red-700 font-bold hover:text-red-900 transition-colors underline"
                        >
                          {content.alternativeLink}
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      </div>
                    </div>
                  </div>
                )}

                {/* Alternative Link */}
                <div className="text-center mb-6">
                  <a 
                    href={thimbleUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-purple-700 font-semibold hover:text-purple-800 transition-colors text-sm"
                  >
                    <ExternalLink className="w-4 h-4" />
                    {language === 'en' ? 'Open in new window' : 'Abrir en nueva ventana'}
                  </a>
                </div>

                {/* Help Section */}
                <div className="mt-8 pt-8 border-t border-gray-200 text-center">
                  <p className="text-gray-600 mb-3">
                    {content.needHelp}
                  </p>
                  <a 
                    href={`tel:${companyInfo.phone}`}
                    className="inline-flex items-center gap-2 text-purple-700 font-bold text-xl hover:text-purple-800 transition-colors"
                  >
                    <Phone className="w-6 h-6" />
                    {companyInfo.phone}
                  </a>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-gray-900">
              {language === 'en' ? 'Coverage Options' : 'Opciones de Cobertura'}
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              {content.benefits.map((benefit, index) => (
                <div key={index} className="flex items-start gap-3">
                  <CheckCircle2 className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                  <p className="text-lg text-gray-700">{benefit}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BusinessInsurance;
